import { ApiErrorCode } from '@dev-console/api/errors';
import type {
  DiscardResult,
  DlqMessageSummary,
  RedriveJobDetail,
  RedriveMode,
  RedriveResult,
  SmartRedriveInfo,
} from '@dev-console/types';
import { useCallback, useState } from 'react';

import { ApiError } from '@/config/api';
import { discardJobs, redriveJobs } from '@/config/api-helpers';
import { useModal } from '@/lib/modal-context';
import { truncate } from '@/lib/utils';

interface RedriveDialogProps {
  mode: 'redrive' | 'discard';
  messages: DlqMessageSummary[];
  dlqUrl: string;
  canonicalJob: string;
  smartRedriveInfo: SmartRedriveInfo | null;
  onConfirm: () => void;
  onCancel: () => void;
}

type DialogState = 'confirm' | 'loading' | 'success' | 'warning';

const RedriveDetailsTable = ({ details }: { details: RedriveJobDetail[] }) => (
  <div className='mt-3 max-h-40 overflow-auto rounded border border-neutral-200'>
    <table className='w-full text-left text-xs'>
      <thead className='border-b border-neutral-200 bg-neutral-50'>
        <tr>
          <th className='px-2 py-1.5 font-medium text-neutral-600'>Job Name</th>
          <th className='px-2 py-1.5 font-medium text-neutral-600'>Trace ID</th>
          <th className='px-2 py-1.5 font-medium text-neutral-600'>New Message ID</th>
        </tr>
      </thead>
      <tbody>
        {details.map((d, i) => (
          <tr key={i} className='border-b border-neutral-100'>
            <td className='px-2 py-1 text-neutral-700'>
              {d.jobName}
              {d.sourceMessageIds.length > 1 && (
                <span className='ml-1 text-neutral-400'>({d.sourceMessageIds.length} DLQ messages)</span>
              )}
            </td>
            <td className='px-2 py-1 font-mono text-neutral-600'>{truncate(d.traceId, 16)}</td>
            <td className='px-2 py-1 font-mono text-neutral-600'>{truncate(d.newMessageId, 16)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export const RedriveDialog = ({
  mode,
  messages,
  dlqUrl,
  canonicalJob,
  smartRedriveInfo,
  onConfirm,
  onCancel,
}: RedriveDialogProps) => {
  const [state, setState] = useState<DialogState>('confirm');
  const [resultMessage, setResultMessage] = useState<string>('');
  const [redriveMode, setRedriveMode] = useState<RedriveMode>(smartRedriveInfo != null ? 'smart' : 'naive');
  const [redriveDetails, setRedriveDetails] = useState<RedriveJobDetail[] | null>(null);
  const modal = useModal();

  const handleConfirm = useCallback(async () => {
    const messageIds = messages.map((m) => m.messageId);
    setState('loading');
    try {
      const data =
        mode === 'redrive'
          ? await redriveJobs({ dlqUrl, messageIds, redriveMode })
          : await discardJobs({ dlqUrl, messageIds });

      if (mode === 'redrive') {
        const result = data as RedriveResult;
        const hasErrors = result.errors.length > 0 || result.jobSendsFailed > 0 || result.dlqDeletesFailed > 0;
        const hasSuccess = result.jobsSent > 0;

        const parts = [`${result.jobsSent} job(s) sent, ${result.dlqMessagesDeleted} DLQ message(s) cleaned up.`];
        if (result.messagesNotFound > 0) parts.push(`${result.messagesNotFound} not found in DLQ.`);
        if (result.errors.length > 0) {
          parts.push(`\n${result.errors.length} issue(s):\n${result.errors.map((e: string) => `  • ${e}`).join('\n')}`);
        }
        const message = parts.join(' ');

        if (hasErrors && !hasSuccess) {
          modal.showJobsDlqError({ action: mode, dlqUrl, messageIds, message });
          onCancel();
          return;
        }

        setState(hasErrors ? 'warning' : 'success');
        setRedriveDetails(result.jobDetails);
        setResultMessage(message);
      } else {
        const result = data as DiscardResult;
        const hasErrors = result.failed > 0 || result.notFound > 0;
        const hasSuccess = result.succeeded > 0;

        const parts = [`${result.succeeded} of ${messages.length} message(s) discarded.`];
        if (result.notFound > 0) parts.push(`${result.notFound} not found.`);
        if (result.failed > 0) parts.push(`${result.failed} failed to delete.`);
        const message = parts.join(' ');

        if (hasErrors && !hasSuccess) {
          modal.showJobsDlqError({ action: mode, dlqUrl, messageIds, message });
          onCancel();
          return;
        }

        setState(hasErrors ? 'warning' : 'success');
        setResultMessage(message);
      }
    } catch (err) {
      if (err instanceof ApiError && err.body.code === ApiErrorCode.POD_FORBIDDEN) {
        modal.showPodForbidden(err.body);
      } else {
        const message = err instanceof Error ? err.message : `Failed to ${mode} messages`;
        modal.showJobsDlqError({ action: mode, dlqUrl, messageIds, message });
      }
      onCancel();
    }
  }, [dlqUrl, messages, mode, redriveMode, modal, onCancel]);

  const title = mode === 'redrive' ? 'Redrive Messages' : 'Discard Messages';
  const actionLabel = mode === 'redrive' ? 'Redrive' : 'Discard';

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40'>
      <div className='mx-4 w-full max-w-lg rounded-lg bg-white shadow-xl'>
        <div className='border-b border-neutral-200 px-6 py-4'>
          <h3 className='text-lg font-medium text-neutral-900'>{title}</h3>
        </div>

        <div className='px-6 py-4'>
          {state === 'confirm' && (
            <>
              {mode === 'redrive' && smartRedriveInfo != null && (
                <div className='mb-4 rounded-md border border-neutral-200 bg-neutral-50 p-3'>
                  <p className='mb-2 text-xs font-medium text-neutral-600'>Redrive Mode</p>
                  <label className='mb-2 flex cursor-pointer items-start gap-2'>
                    <input
                      type='radio'
                      name='redriveMode'
                      value='smart'
                      checked={redriveMode === 'smart'}
                      onChange={() => setRedriveMode('smart')}
                      className='mt-0.5'
                    />
                    <div>
                      <span className='text-sm font-medium text-neutral-800'>Smart</span>
                      <span className='ml-1 text-xs text-neutral-500'>
                        (replay from root: <code className='text-xs'>{smartRedriveInfo.rootJobName}</code>)
                      </span>
                      <p className='mt-0.5 text-xs text-neutral-500'>{smartRedriveInfo.description}</p>
                    </div>
                  </label>
                  <label className='flex cursor-pointer items-start gap-2'>
                    <input
                      type='radio'
                      name='redriveMode'
                      value='naive'
                      checked={redriveMode === 'naive'}
                      onChange={() => setRedriveMode('naive')}
                      className='mt-0.5'
                    />
                    <div>
                      <span className='text-sm font-medium text-neutral-800'>Naive</span>
                      <span className='ml-1 text-xs text-neutral-500'>(replay exact failed message)</span>
                    </div>
                  </label>
                </div>
              )}

              <p className='mb-3 text-sm text-neutral-700'>
                {mode === 'redrive' && redriveMode === 'smart'
                  ? `This will resolve signUpLeadIds from ${messages.length} message(s) and replay from ${
                      smartRedriveInfo?.rootJobName ?? 'root'
                    }. All downstream jobs will re-execute.`
                  : mode === 'redrive'
                  ? `This will redrive ${messages.length} message(s) back to the main queue for ${canonicalJob}.`
                  : `This will permanently discard ${messages.length} message(s) from the DLQ for ${canonicalJob}. This action cannot be undone.`}
              </p>

              <div className='max-h-48 overflow-auto rounded-md border border-neutral-200'>
                <table className='w-full text-left text-xs'>
                  <thead className='border-b border-neutral-200 bg-neutral-50'>
                    <tr>
                      <th className='px-3 py-2 font-medium text-neutral-600'>Message ID</th>
                      <th className='px-3 py-2 font-medium text-neutral-600'>Job Name</th>
                      <th className='px-3 py-2 font-medium text-neutral-600'>Trace ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((msg) => (
                      <tr key={msg.messageId} className='border-b border-neutral-100'>
                        <td className='px-3 py-1.5 font-mono text-neutral-700'>{truncate(msg.messageId, 12)}</td>
                        <td className='px-3 py-1.5 text-neutral-700'>{msg.jobName ?? '—'}</td>
                        <td className='px-3 py-1.5 font-mono text-neutral-600'>
                          {msg.traceId ? truncate(msg.traceId, 12) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {state === 'loading' && (
            <div className='flex items-center justify-center py-8'>
              <span className='text-sm text-neutral-500'>
                {mode === 'redrive' ? 'Redriving' : 'Discarding'} {messages.length} message(s)...
              </span>
            </div>
          )}

          {state === 'success' && (
            <div className='rounded-md border border-green-200 bg-green-50 p-4'>
              <p className='whitespace-pre-wrap text-sm text-green-700'>{resultMessage}</p>
              {redriveDetails != null && redriveDetails.length > 0 && <RedriveDetailsTable details={redriveDetails} />}
            </div>
          )}

          {state === 'warning' && (
            <div className='rounded-md border border-amber-200 bg-amber-50 p-4'>
              <p className='whitespace-pre-wrap text-sm text-amber-700'>{resultMessage}</p>
              {redriveDetails != null && redriveDetails.length > 0 && <RedriveDetailsTable details={redriveDetails} />}
            </div>
          )}
        </div>

        <div className='flex justify-end gap-2 border-t border-neutral-200 px-6 py-4'>
          {state === 'confirm' && (
            <>
              <button
                onClick={onCancel}
                className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                className={`rounded-md px-4 py-2 text-sm font-medium text-white ${
                  mode === 'redrive' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {actionLabel} {messages.length} Message(s)
              </button>
            </>
          )}

          {(state === 'success' || state === 'warning') && (
            <button
              onClick={onConfirm}
              className='rounded-md border border-neutral-200 px-4 py-2 text-sm text-neutral-700 hover:bg-neutral-50'
            >
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
